import { useMemo } from 'react'
import styles from '../RegistrationSideCard.module.scss'
import classNames from 'classnames'
import { getCapacityState, getEnergyNameI18Key, getProducerLocation } from 'utils/producers'
import { DeliveryAddressFieldKeys } from 'store/customer/enums'
import PriceSimulationSection from 'features/registration/components/registration-side-card/price-simulation-section/PriceSimulationSection'
import UsageSection from 'features/registration/components/registration-side-card/usage-section/UsageSection'
import SalesPricesSection from 'features/registration/components/registration-side-card/sales-prices-section/SalesPricesSection'
import PromoCode from 'components/promo-code/PromoCode'
import SimulationRegistrationSideCardLayout from 'components/simulation-registration-side-card-layout/SimulationRegistrationSideCardLayout'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next/pages'
import useWindowSize from 'hooks/useWindowSize'
import { SimulationCardContentProps } from './types'
import { Language } from 'types/language'
import { useStoreSelector } from 'hooks/store'
import { checkHasEditUsageDrawer, checkHasPriceSimulationSection } from 'features/registration/utils'
import { Icon } from '@boltenergy-be/design-system'
import { EnergyType } from 'types/producer'

const SimulationCardContent = ({
  producer,
  setShowProducerDrawer,
  setShowPriceDrawer,
  setShowFixedPricesDrawer,
  setShowEditInstalmentDrawer,
  setShowEditUsageDrawer
}: SimulationCardContentProps) => {
  // Redux store
  const { simulation, deliveryAddress } = useStoreSelector((store) => store.customer.inputs)
  const { confirmationFromPortalRegistration } = useStoreSelector((store) => store.marketing)

  // Next router
  const router = useRouter()
  const { locale } = router

  // I18n
  const { t } = useTranslation(['simulationCard', 'common', 'producers', 'producer'])

  // Custom hooks
  const { isTablet } = useWindowSize()

  // Constants
  const capacityState = useMemo(() => getCapacityState(producer, simulation, true), [simulation, producer])
  const shouldShowPriceSimulationSection = checkHasPriceSimulationSection(simulation.chosenSimulationType)
  const shouldShowEditUsageDrawer = checkHasEditUsageDrawer(simulation.chosenSimulationType)

  return (
    <SimulationRegistrationSideCardLayout
      className={styles['card-content']}
      hideProducerImage={isTablet}
      hideProducerLocation
      {...{ producer }}
    >
      {/* SELECTED PRODUCER NAME */}
      <section className={classNames(styles.section, styles['selected-producer'])}>
        <div className={styles['el-product']}>
          <Icon name="electricity" />
          <span className={styles.location}>
            {t(`energyTypeAndLocation.${producer.energyType === EnergyType.BIO ? 'bio' : 'other'}`, {
              energyType:
                locale === Language.FRENCH
                  ? t(getEnergyNameI18Key(producer.energyType)).toLowerCase()
                  : t(getEnergyNameI18Key(producer.energyType)),
              location: getProducerLocation(locale, producer?.location)
            })}
          </span>
          <strong className={classNames(styles.producer, 'h4 serif')}>{producer.name[locale]}</strong>
        </div>

        <SimulationRegistrationSideCardLayout.Action onClick={() => setShowProducerDrawer(true)}>
          {t('common:change', 'Wijzig')}
        </SimulationRegistrationSideCardLayout.Action>

        {capacityState.soldOut && <p className={classNames('is-error-text', styles['over-capacity'])}>{t('producer:notEnoughCapacity')}</p>}
      </section>

      <hr />

      {deliveryAddress[DeliveryAddressFieldKeys.NEEDS_GAS] && (
        <>
          <section className={classNames(styles.section)}>
            <div className={styles['gas-product']}>
              <Icon name="gas" />
              <span className={styles.product}>{t('common:gasFromMarket', 'Gas van de groothandelsmarkt')}</span>
            </div>
          </section>
          <hr />
        </>
      )}

      {shouldShowPriceSimulationSection ? (
        <>
          {/* PRICE SIMULATION */}
          <PriceSimulationSection {...{ setShowEditInstalmentDrawer, setShowPrice: setShowPriceDrawer }} />
          <hr />

          {shouldShowEditUsageDrawer && (
            <>
              <UsageSection {...{ setShowEditUsageDrawer }} />
              <hr />
            </>
          )}
        </>
      ) : (
        <>
          <SalesPricesSection {...{ setShowFixedPricesDrawer }} />
          <hr />
        </>
      )}

      {/* PROMO */}
      {!confirmationFromPortalRegistration && (
        <section className={classNames(styles.section, styles.promo)}>
          <PromoCode variant="side-card" className={styles['promo-line']} />
        </section>
      )}
    </SimulationRegistrationSideCardLayout>
  )
}

export default SimulationCardContent
